import React from "react";
import { Box, Button, ButtonGroup, Stack } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import {
  OktonEmptyState,
  OktonLoadingState,
  OktonPageHeader,
  OktonPanel,
  OktonSelect,
  useOktonAuth,
  useOktonPlatform,
} from "@okton/platform-ui";
import { AiPlanReconciliationPanel } from "../features/ai-plans/AiPlanReconciliationPanel";
import { AiPlanAdmin } from "../features/ai-plans/AiPlanAdmin";
import { AiPlanPilotPanel } from "../features/ai-plans/AiPlanPilotPanel";
import { AiBudgetPanel } from "../features/ai-budget/AiBudgetPanel";
import { AiProviderPanel } from "../features/ai-provider/AiProviderPanel";
import { AiRouterPanel } from "../features/ai-provider/AiRouterPanel";
import { AiLearningPanel } from "../features/ai-learning/AiLearningPanel";
import { AiExecutionPanel } from "../features/ai-execution/AiExecutionPanel";
import { AiSourcesEditor } from "../features/ai-sources/AiSourcesEditor";

export type AiSection = "overview" | "budget" | "provider" | "router" | "learning" | "systems" | "reconciliation" | "plans" | "pilot";

const titles: Record<AiSection,{title:string;subtitle:string}> = {
  overview: { title:"IA Okton", subtitle:"Administração central da plataforma de inteligência artificial da Okton." },
  budget: { title:"Orçamento", subtitle:"Controle de teto, consumo e disponibilidade financeira da IA." },
  provider: { title:"Modelos e tarifas", subtitle:"Configuração atual de modelos e versões de tarifa." },
  router: { title:"AI Router", subtitle:"Catálogo, perfis e simulação de roteamento por custo e capacidade." },
  learning: { title:"Aprendizado", subtitle:"Answer Library, candidatos e sinais de aprendizado da IA Okton." },
  systems: { title:"IA por sistema", subtitle:"Fontes, inventário e execuções administrativas por sistema." },
  reconciliation: { title:"Conferências", subtitle:"Conferência e liberação de solicitações da IA." },
  plans: { title:"Planos de IA", subtitle:"Administração dos planos e capacidades comerciais da IA." },
  pilot: { title:"Teste do piloto", subtitle:"Teste real do assistente contratado com Router, custo e recuperação." },
};

export const AiPage: React.FC<{ section: AiSection }> = ({ section }) => {
  const history = useHistory();
  const { accessToken } = useOktonAuth();
  const { applications, isLoading } = useOktonPlatform();
  const [systemId, setSystemId] = React.useState("");
  const selected = applications.find(application => application.id === systemId);
  const heading=titles[section];

  const content = (() => {
    if(section==="overview"){
      return <Stack spacing="6">
        <OktonPanel title="Painel administrativo da IA Okton" subtitle="Use o menu lateral para administrar orçamento, modelos, Router, sistemas, conferências e planos.">
          <Box />
        </OktonPanel>
        <OktonPanel title="Estado da arquitetura" subtitle="O AI Router está sendo preparado em dry-run antes da ativação de execução real.">
          <ButtonGroup size="sm">
            <Button colorScheme="blue" onClick={() => history.push("/router")}>Continuar configuração</Button>
            <Button variant="outline" onClick={() => history.push("/budget")}>Revisar orçamento</Button>
          </ButtonGroup>
        </OktonPanel>
      </Stack>;
    }
    if(section==="budget") return <AiBudgetPanel accessToken={accessToken} />;
    if(section==="provider") return <AiProviderPanel accessToken={accessToken} />;
    if(section==="router") return <AiRouterPanel accessToken={accessToken} />;
    if(section==="learning") return <AiLearningPanel accessToken={accessToken} />;
    if(section==="reconciliation") return <AiPlanReconciliationPanel accessToken={accessToken} />;
    if(section==="plans") return accessToken ? <AiPlanAdmin token={accessToken} /> : null;
    if(section==="pilot") return <AiPlanPilotPanel />;

    return <Stack spacing="6">
      <OktonPanel title="Sistema" subtitle="Selecione o sistema para configurar fontes, consultar o inventário e acompanhar execuções.">
        {isLoading ? (
          <OktonLoadingState title="Carregando sistemas..." minH="120px" />
        ) : applications.length === 0 ? (
          <OktonEmptyState title="Nenhum sistema disponível" description="Não há sistemas liberados para este acesso." />
        ) : (
          <OktonSelect name="ai-system" label="Sistema" value={selected?.id || ""} onChange={event => setSystemId(event.target.value)}>
            <option value="">Selecione o sistema</option>
            {applications.map(application => (
              <option key={application.id} value={application.id}>{application.name}</option>
            ))}
          </OktonSelect>
        )}
      </OktonPanel>
      {selected && accessToken && (
        <Stack key={selected.id} spacing="6">
          <AiSourcesEditor systemId={selected.id} accessToken={accessToken} />
          <AiExecutionPanel systemId={selected.id} accessToken={accessToken} active={!isLoading} />
        </Stack>
      )}
    </Stack>;
  })();

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 12]} w="100%">
      <OktonPageHeader title={heading.title} subtitle={heading.subtitle} />
      {content}
    </Box>
  );
};
